/**
 * The OS file dialog that fronts the upload takeover. Callers render `input`
 * somewhere in their tree and call `open()` from their Upload button; `onPick`
 * fires only once the user actually chose something (cancelling is a no-op).
 */
import React, { useCallback, useRef } from "react";
import { describeFiles, isOversize, isTooLargeForBatch, MAX_UPLOAD_FILES } from "./files";
import type { SkippedFiles, UploadFileDescriptor } from "./types";

/** Extensions the Vault accepts, offered as the dialog's default filter. */
export const UPLOAD_ACCEPT =
  ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.png,.jpg,.jpeg,.gif,.zip";

/**
 * Fires the OS dialog and hands back at most {@link MAX_UPLOAD_FILES} descriptors —
 * picking more caps the selection to the first ten and calls `onExceedLimit` (if
 * given) with how many were dropped, so the caller can tell the user.
 *
 * @param onExceedLimit
 * @deprecated Use `onSkip`, which reports the document cap and the batch size
 * cap together. This only ever hears about the former, so a pick that trips both
 * tells the caller half the story. Ignored entirely when `onSkip` is given —
 * passing both would toast twice for one pick.
 *
 * @param multiple Whether the OS dialog allows picking more than one file.
 * Defaults to `true` (the bulk-upload takeover this hook was built for). A
 * caller that stages exactly one file (e.g. a version-replace picker) should
 * pass `false` so a multi-select pick can't happen in the first place, rather
 * than relying on the skip report to explain a truncation after the fact.
 *
 * @param onSkip Called at most once per pick with everything that was dropped —
 * the document cap and the batch size cap (DV-774) in one {@link SkippedFiles}.
 * Wiring it also ENABLES the batch size cap: without it, over-cap files are left
 * in the selection rather than removed with nobody to report it, and the modal
 * blocks them instead.
 */
export function useUploadFilePicker(
  onPick: (files: UploadFileDescriptor[]) => void,
  onExceedLimit?: (dropped: number) => void,
  multiple = true,
  onSkip?: (skipped: SkippedFiles) => void,
) {
  const ref = useRef<HTMLInputElement>(null);

  const open = useCallback(() => {
    ref.current?.click();
  }, []);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const chosen = Array.from(e.target.files ?? []);
      // Reset first so re-picking the same file still fires a change event.
      e.target.value = "";
      if (chosen.length === 0) return;
      // The batch API is single-part per file, so a file DSS would send multipart
      // can't ride in a batch — drop it here rather than let the whole round trip
      // fail with a storage-layer string (DV-774). Runs BEFORE the count cap so
      // the cap fills from files that can actually be batched: capping first
      // staged nine when ten would fit.
      //
      // Four conditions, all load-bearing:
      //  - a multi-file pick, since a lone file takes the single-file path
      //  - `multiple`, since a single-select picker is never a batch
      //  - `onSkip`, so we only remove what we can TELL the caller about
      //  - under `MAX_UPLOAD_BYTES`, so "add it on its own" is true advice
      //
      // Anything left in here still can't reach the batch API: the modal gates
      // submit on the same predicate and explains it with a Remove action.
      const oversizeNames: string[] = [];
      let candidates = chosen;
      if (onSkip && multiple && chosen.length > 1) {
        // Wrapped as a descriptor: the predicates read `bytes`/`source`, and a raw
        // `File` has neither, so handing them one would quietly never match.
        const batchableAlone = (file: File) => {
          const descriptor = { source: file };
          return isTooLargeForBatch(descriptor) && !isOversize(descriptor);
        };
        candidates = chosen.filter((file) => !batchableAlone(file));
        oversizeNames.push(...chosen.filter(batchableAlone).map((file) => file.name));
      }

      // The OS dialog itself already enforces single-select when `multiple` is
      // false, but capping here too means a bad selection is still bounded to
      // one rather than silently uploading the first of several.
      const cap = multiple ? MAX_UPLOAD_FILES : 1;
      const keep = candidates.slice(0, cap);
      const droppedForCount = candidates.length - keep.length;

      // ONE report for both reasons. Callers render this into a single-slot
      // toast, so two calls in the same change event meant the second silently
      // replaced the first and the user only heard half of it. `oversizeNames` is
      // only ever populated when `onSkip` exists, so the fallback below can only
      // see count drops — precisely what the deprecated callback reports.
      if (droppedForCount > 0 || oversizeNames.length > 0) {
        if (onSkip) onSkip({ droppedForCount, oversizeNames });
        else if (droppedForCount > 0) onExceedLimit?.(droppedForCount);
      }

      // Nothing survived. Returning without calling `onPick` matters: the takeover
      // falls back to its SAMPLE_FILES demo set on an empty `initialFiles`, so
      // handing it [] would show a real user fake documents.
      if (keep.length === 0) return;
      void describeFiles(keep).then(onPick);
    },
    [onPick, onExceedLimit, multiple, onSkip],
  );

  const input = (
    <input
      ref={ref}
      type="file"
      multiple={multiple}
      accept={UPLOAD_ACCEPT}
      hidden
      // `hidden` alone loses to any author-level `display` rule on inputs.
      style={{ display: "none" }}
      data-testid="upload-file-input"
      onChange={onChange}
    />
  );

  return { open, input };
}
